import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import {
  getAxisColorScheme,
  normalizeCrosshairSnapMode,
  normalizeCrosshairPriceLabelPreference,
  normalizeAdaptiveGridPreference,
} from '../../../constants/chartPreferences';
import {
  fetchUserSettings,
  saveUserSettings,
  USER_SETTING_CHART_PREFERENCES,
} from '../../userSettings/userSettingsSlice';
import {
  normalizeNumericArray,
  normalizeStringArray,
  arraysEqual,
  normalizeBooleanPreference,
  BOOLEAN_CHART_PREFERENCE_KEYS,
  STRING_ARRAY_CHART_PREFERENCE_KEYS,
  shallowPreferencesEqual,
} from '../marketDetailUtils';

/**
 * The chartPreferences map with one symbol's entry replaced. A symbol with nothing left to save
 * is sent as null under every spelling it is stored by. From SPT #708 the API merges the map per
 * symbol, deleting a null entry and keeping one left out; before it, the map is stored whole as
 * sent, null included (DWLF-507).
 */
export const withSymbolPreferences = ({ store, symbol, symbolKeys, persistable }) => {
  const next = { ...store };
  if (Object.keys(persistable).length > 0) {
    next[symbol] = persistable;
    return next;
  }
  symbolKeys.forEach((key) => {
    if (next[key] != null) next[key] = null;
  });
  return next;
};

/** The chart preferences that are saved: a whitelist, so a key missing here is never persisted. */
export const persistableChartPreferences = (chartPreferences) => {
  const source = chartPreferences || {};
  const result = {};

  const assignIfDefined = (key, value) => {
    if (value === undefined) {
      return;
    }
    if (Array.isArray(value)) {
      result[key] = [...value];
    } else {
      result[key] = value;
    }
  };

  assignIfDefined('timeframe', source.timeframe);

  assignIfDefined('showDailyTrendlines', source.showDailyTrendlines);

  const normalizedWeekly = normalizeBooleanPreference(
    source.showWeeklyTrendlines,
  );
  if (typeof normalizedWeekly === 'boolean') {
    result.showWeeklyTrendlines = normalizedWeekly;
  }

  const normalizedHourly = normalizeBooleanPreference(
    source.showHourlyTrendlines,
  );
  if (typeof normalizedHourly === 'boolean') {
    result.showHourlyTrendlines = normalizedHourly;
  }
  assignIfDefined('showSwings', source.showSwings);
  const normalizedSystemTrades = normalizeBooleanPreference(source.showSystemTrades);
  if (typeof normalizedSystemTrades === 'boolean') {
    result.showSystemTrades = normalizedSystemTrades;
  }
  const normalizedUserTrades = normalizeBooleanPreference(source.showUserTrades);
  if (typeof normalizedUserTrades === 'boolean') {
    result.showUserTrades = normalizedUserTrades;
  }
  const normalizedTradeJournal = normalizeBooleanPreference(source.showTradeJournal);
  if (typeof normalizedTradeJournal === 'boolean') {
    result.showTradeJournal = normalizedTradeJournal;
  }
  assignIfDefined('showTrades', source.showTrades);
  assignIfDefined('showFib', source.showFib);
  assignIfDefined('showSR', source.showSR);
  assignIfDefined('showDSS', source.showDSS);
  assignIfDefined('showATR', source.showATR);
  assignIfDefined('showRSI', source.showRSI);
  assignIfDefined('showADX', source.showADX);
  assignIfDefined('showStages', source.showStages);
  assignIfDefined('showBollinger', source.showBollinger);
  assignIfDefined('eventLayers', source.eventLayers);
  assignIfDefined('showEMACloud', source.showEMACloud);
  assignIfDefined('showWeeklyCycleLows', source.showWeeklyCycleLows);
  assignIfDefined('showWeeklyCycleHighs', source.showWeeklyCycleHighs);
  assignIfDefined('showLiveWeeklyCycleLows', source.showLiveWeeklyCycleLows);
  assignIfDefined('showLiveWeeklyCycleHighs', source.showLiveWeeklyCycleHighs);
  assignIfDefined('showCycleLowWindow', source.showCycleLowWindow);
  assignIfDefined('showCycleHighWindow', source.showCycleHighWindow);
  assignIfDefined('showPastLowWindows', source.showPastLowWindows);
  assignIfDefined('showPastHighWindows', source.showPastHighWindows);
  assignIfDefined('showCycleTrendlines', source.showCycleTrendlines);
  assignIfDefined('activeSMAs', source.activeSMAs);
  assignIfDefined('activeEMAs', source.activeEMAs);
  assignIfDefined('vwapAnchors', source.vwapAnchors);
  assignIfDefined('smcOverlays', source.smcOverlays);
  if (Array.isArray(source.customEventSelections)) {
    result.customEventSelections = normalizeStringArray(
      source.customEventSelections,
    );
  }
  if (Array.isArray(source.userStrategySelections)) {
    result.userStrategySelections = normalizeStringArray(
      source.userStrategySelections,
    );
  }
  assignIfDefined('tradeSignalType', source.tradeSignalType);
  assignIfDefined('selectedStrategy', source.selectedStrategy);
  return result;
};

/**
 * Hook for managing chart preferences with debounced auto-save.
 * 
 * @param {Object} options
 * @param {string} options.symbol - The current symbol (e.g., 'BTC-USD')
 * @param {string} options.selectedTimeframe - The selected timeframe (e.g., 'daily')
 * @param {Object} options.userSettings - The user settings object from Redux
 * @param {boolean} options.userSettingsLoading - Whether user settings are currently loading
 * @param {boolean} options.userSettingsSynced - Whether user settings were actually read from the
 *   server for the signed-in account (selectUserSettingsSynced). Nothing is saved until they were.
 * @param {Object} options.timeframeOptions - Available timeframe options
 * 
 * @returns {Object} - { preferences, setPreference, isDirty, save, reset, ...derived }
 */
const useChartPreferences = ({
  symbol: formattedSymbol,
  selectedTimeframe,
  userSettings,
  userSettingsLoading,
  userSettingsSynced = false,
  // Current UI state values to sync back to preferences
  tradeSignalType,
  selectedStrategy,
  // Pending timeframe from parent (used to guard against overwriting saved preference during hydration)
  pendingPreferredTimeframe,
  // Callback to reset pending timeframe in parent
  onResetPendingTimeframe,
}) => {
  const dispatch = useDispatch();

  // Local state for chart preferences
  const [chartPreferences, setChartPreferences] = useState({});
  const [chartPreferencesDirty, setChartPreferencesDirty] = useState(false);
  const [appliedChartPreferencesSymbol, setAppliedChartPreferencesSymbol] = useState(null);
  
  // Refs
  const chartPreferencesSaveTimeoutRef = useRef(null);
  // Set when this symbol's preferences were applied from settings that were NOT the server's
  // (a failed read leaves them empty). Those defaults must never be saved: they would replace this
  // symbol's real preferences and, until SPT #708 merges the map per symbol, every other symbol's.
  const appliedWhileUnsyncedRef = useRef(false);
  const refetchRequestedRef = useRef(false);

  // Clear pending save timeout
  const clearPendingChartPreferencesSave = useCallback(() => {
    if (chartPreferencesSaveTimeoutRef.current) {
      clearTimeout(chartPreferencesSaveTimeoutRef.current);
      chartPreferencesSaveTimeoutRef.current = null;
    }
  }, []);

  // Get chart preferences store from user settings
  const chartPreferencesStore = useMemo(() => {
    const raw = userSettings?.[USER_SETTING_CHART_PREFERENCES];
    if (raw && typeof raw === 'object') {
      return raw;
    }
    return {};
  }, [userSettings]);

  // Get global chart preferences
  const globalChartPreferences = useMemo(() => {
    const raw = chartPreferencesStore?.__global;
    if (raw && typeof raw === 'object') {
      return raw;
    }
    return {};
  }, [chartPreferencesStore]);

  // Global preference derived values
  const axisColorSchemeId = globalChartPreferences.axisColorScheme;
  const axisColorScheme = useMemo(
    () => getAxisColorScheme(axisColorSchemeId),
    [axisColorSchemeId],
  );
  const axisColors = axisColorScheme.colors;
  const crosshairSnapMode = normalizeCrosshairSnapMode(globalChartPreferences.crosshairSnapMode);
  const showCrosshairPriceLabel = normalizeCrosshairPriceLabelPreference(
    globalChartPreferences.showCrosshairPriceLabel,
  );
  const showAdaptiveGrid = normalizeAdaptiveGridPreference(globalChartPreferences.showAdaptiveGrid);
  const showAdminChartControls = globalChartPreferences.showAdminChartControls === true;

  // Symbol-specific saved preferences
  const symbolKeyVariants = useMemo(() => {
    if (!formattedSymbol) {
      return [];
    }
    const hyphenated = formattedSymbol.replace('/', '-');
    const slashed = formattedSymbol.replace('-', '/');
    return Array.from(new Set([formattedSymbol, hyphenated, slashed].filter(Boolean)));
  }, [formattedSymbol]);

  const savedChartPreferences = useMemo(() => {
    if (!formattedSymbol) {
      return null;
    }
    for (const key of symbolKeyVariants) {
      const candidate = chartPreferencesStore?.[key];
      if (candidate && typeof candidate === 'object') {
        return candidate;
      }
    }
    return null;
  }, [chartPreferencesStore, symbolKeyVariants, formattedSymbol]);

  // Whether preferences are ready for the current symbol
  const chartPreferencesReadyForSymbol = useMemo(
    () =>
      Boolean(formattedSymbol) &&
      appliedChartPreferencesSymbol === formattedSymbol,
    [appliedChartPreferencesSymbol, formattedSymbol],
  );

  // Saved trendline visibility preferences
  const savedWeeklyTrendlinePreference = useMemo(
    () => normalizeBooleanPreference(chartPreferences?.showWeeklyTrendlines),
    [chartPreferences],
  );

  const savedDailyTrendlinePreference = useMemo(
    () => normalizeBooleanPreference(chartPreferences?.showDailyTrendlines),
    [chartPreferences],
  );

  const savedHourlyTrendlinePreference = useMemo(
    () => normalizeBooleanPreference(chartPreferences?.showHourlyTrendlines),
    [chartPreferences],
  );

  // Derived trendline visibility
  const showDailyTrendlines = useMemo(() => {
    if (typeof savedDailyTrendlinePreference === 'boolean') {
      return savedDailyTrendlinePreference;
    }
    return true;
  }, [savedDailyTrendlinePreference]);

  const showWeeklyTrendlines = useMemo(() => {
    if (typeof savedWeeklyTrendlinePreference === 'boolean') {
      return savedWeeklyTrendlinePreference;
    }
    return true;
  }, [savedWeeklyTrendlinePreference]);

  const showHourlyTrendlines = useMemo(() => {
    if (typeof savedHourlyTrendlinePreference === 'boolean') {
      return savedHourlyTrendlinePreference;
    }
    return selectedTimeframe === 'hourly';
  }, [savedHourlyTrendlinePreference, selectedTimeframe]);

  // Update preferences
  const updateChartPreferences = useCallback(
    (updates, { markDirty = true } = {}) => {
      if (!updates || typeof updates !== 'object') {
        return;
      }

      setChartPreferences((prev) => {
        let changed = false;
        const next = { ...prev };

        Object.entries(updates).forEach(([key, value]) => {
          if (value === undefined) {
            if (Object.prototype.hasOwnProperty.call(next, key)) {
              delete next[key];
              changed = true;
            }
            return;
          }

          if (BOOLEAN_CHART_PREFERENCE_KEYS.has(key)) {
            const normalized = normalizeBooleanPreference(value);
            const previous = normalizeBooleanPreference(prev[key]);

            if (typeof normalized === 'boolean') {
              if (normalized !== previous) {
                next[key] = normalized;
                changed = true;
              }
            } else if (prev[key] !== undefined) {
              delete next[key];
              changed = true;
            }
            return;
          }

          if (Array.isArray(value)) {
            const normalized = STRING_ARRAY_CHART_PREFERENCE_KEYS.has(key)
              ? normalizeStringArray(value)
              : normalizeNumericArray(value);
            const previous = Array.isArray(prev[key]) ? prev[key] : [];
            if (!arraysEqual(previous, normalized)) {
              next[key] = normalized;
              changed = true;
            }
            return;
          }

          if (prev[key] !== value) {
            next[key] = value;
            changed = true;
          }
        });

        if (!changed) {
          return prev;
        }

        if (markDirty) {
          setChartPreferencesDirty(true);
        }
        return next;
      });
    },
    [],
  );

  // Handle chart preferences change from chart component
  const handleChartPreferencesChange = useCallback(
    (updates) => {
      if (!updates || typeof updates !== 'object') {
        return;
      }

      const normalized = {};
      Object.entries(updates).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          normalized[key] = STRING_ARRAY_CHART_PREFERENCE_KEYS.has(key)
            ? normalizeStringArray(value)
            : normalizeNumericArray(value);
        } else if (BOOLEAN_CHART_PREFERENCE_KEYS.has(key)) {
          const normalizedValue = normalizeBooleanPreference(value);
          if (typeof normalizedValue === 'boolean') {
            normalized[key] = normalizedValue;
          }
        } else {
          normalized[key] = value;
        }
      });

      updateChartPreferences(normalized, {
        markDirty: chartPreferencesReadyForSymbol,
      });
    },
    [chartPreferencesReadyForSymbol, updateChartPreferences],
  );

  // Toggle handlers
  const handleToggleWeeklyTrendlines = useCallback(
    (value) => {
      if (typeof value !== 'boolean') {
        return;
      }
      handleChartPreferencesChange({ showWeeklyTrendlines: value });
    },
    [handleChartPreferencesChange],
  );

  const handleToggleDailyTrendlines = useCallback(
    (value) => {
      if (typeof value !== 'boolean') {
        return;
      }
      handleChartPreferencesChange({ showDailyTrendlines: value });
    },
    [handleChartPreferencesChange],
  );

  const handleToggleHourlyTrendlines = useCallback(
    (value) => {
      if (typeof value !== 'boolean') {
        return;
      }
      handleChartPreferencesChange({ showHourlyTrendlines: value });
    },
    [handleChartPreferencesChange],
  );

  // Get persistable preferences for saving
  const getPersistableChartPreferences = useCallback(
    () => persistableChartPreferences(chartPreferences),
    [chartPreferences],
  );

  // The settings to save for this symbol's preferences, or null when they match what is saved.
  const buildSavePayload = useCallback(() => {
    const persistable = getPersistableChartPreferences();
    if (shallowPreferencesEqual(savedChartPreferences, persistable)) {
      return null;
    }
    const nextStore = withSymbolPreferences({
      store: chartPreferencesStore, symbol: formattedSymbol, symbolKeys: symbolKeyVariants, persistable,
    });
    return { ...userSettings, [USER_SETTING_CHART_PREFERENCES]: nextStore };
  }, [
    getPersistableChartPreferences,
    savedChartPreferences,
    chartPreferencesStore,
    formattedSymbol,
    symbolKeyVariants,
    userSettings,
  ]);

  // Manual save function
  const save = useCallback(() => {
    if (!formattedSymbol || !userSettingsSynced || appliedWhileUnsyncedRef.current) {
      return Promise.resolve();
    }

    const payload = buildSavePayload();
    if (!payload) {
      setChartPreferencesDirty(false);
      return Promise.resolve();
    }

    return dispatch(saveUserSettings(payload)).then(() => {
      setChartPreferencesDirty(false);
    });
  }, [formattedSymbol, userSettingsSynced, buildSavePayload, dispatch]);

  // Reset preferences
  const reset = useCallback(() => {
    setChartPreferences({});
    setChartPreferencesDirty(false);
    setAppliedChartPreferencesSymbol(null);
  }, []);

  // Mark preferences as loaded for a symbol
  const markAsReady = useCallback((symbol) => {
    setAppliedChartPreferencesSymbol(symbol);
  }, []);

  // Cleanup effect
  useEffect(() => {
    return () => {
      clearPendingChartPreferencesSave();
    };
  }, [clearPendingChartPreferencesSave]);

  // Reset on symbol change
  useEffect(() => {
    clearPendingChartPreferencesSave();
    setChartPreferences({});
    setChartPreferencesDirty(false);
    setAppliedChartPreferencesSymbol(null);
    appliedWhileUnsyncedRef.current = false;
    refetchRequestedRef.current = false;
    // Reset pending timeframe in parent if callback provided
    if (onResetPendingTimeframe) {
      onResetPendingTimeframe();
    }
  }, [formattedSymbol, clearPendingChartPreferencesSave, onResetPendingTimeframe]);

  // Sync selectedTimeframe to chartPreferences when it changes
  useEffect(() => {
    // Guard: don't overwrite saved preference during hydration when pending timeframe exists
    if (pendingPreferredTimeframe) {
      return;
    }
    if (!selectedTimeframe || !chartPreferencesReadyForSymbol) {
      return;
    }
    updateChartPreferences({ timeframe: selectedTimeframe }, { markDirty: true });
  }, [selectedTimeframe, chartPreferencesReadyForSymbol, pendingPreferredTimeframe]);

  // Sync tradeSignalType to chartPreferences when it changes
  useEffect(() => {
    if (!tradeSignalType || !chartPreferencesReadyForSymbol) {
      return;
    }
    updateChartPreferences({ tradeSignalType }, { markDirty: true });
  }, [tradeSignalType, chartPreferencesReadyForSymbol]);

  // Sync selectedStrategy to chartPreferences when it changes
  useEffect(() => {
    if (!selectedStrategy || !chartPreferencesReadyForSymbol) {
      return;
    }
    updateChartPreferences({ selectedStrategy }, { markDirty: true });
  }, [selectedStrategy, chartPreferencesReadyForSymbol]);

  // Remember when this symbol was hydrated from settings that were not the server's.
  useEffect(() => {
    if (appliedChartPreferencesSymbol && !userSettingsSynced) {
      appliedWhileUnsyncedRef.current = true;
    }
  }, [appliedChartPreferencesSymbol, userSettingsSynced]);

  // When the server's settings arrive after all, throw the unsynced hydration away — including
  // any edits made on top of those defaults — so the page re-applies the real saved preferences.
  useEffect(() => {
    if (!userSettingsSynced || !appliedWhileUnsyncedRef.current) {
      return;
    }
    appliedWhileUnsyncedRef.current = false;
    clearPendingChartPreferencesSave();
    setChartPreferences({});
    setChartPreferencesDirty(false);
    setAppliedChartPreferencesSymbol(null);
  }, [userSettingsSynced, clearPendingChartPreferencesSave]);

  // Edits waiting on a read that failed: ask for the settings once more per symbol (the fetch
  // itself retries transient failures). Until a read succeeds the edits stay local and unsaved.
  useEffect(() => {
    if (!chartPreferencesDirty || userSettingsSynced || userSettingsLoading || refetchRequestedRef.current) {
      return;
    }
    refetchRequestedRef.current = true;
    dispatch(fetchUserSettings({ force: true }));
  }, [chartPreferencesDirty, userSettingsSynced, userSettingsLoading, dispatch]);

  // Auto-save effect with debounce
  useEffect(() => {
    if (!chartPreferencesDirty) {
      return;
    }

    if (
      !chartPreferencesReadyForSymbol ||
      !formattedSymbol ||
      !userSettingsSynced ||
      appliedWhileUnsyncedRef.current
    ) {
      return;
    }

    clearPendingChartPreferencesSave();

    chartPreferencesSaveTimeoutRef.current = setTimeout(() => {
      const payload = buildSavePayload();
      if (payload) {
        dispatch(saveUserSettings(payload));
      }
      setChartPreferencesDirty(false);
    }, 750);

    return () => {
      clearPendingChartPreferencesSave();
    };
  }, [
    chartPreferencesDirty,
    chartPreferencesReadyForSymbol,
    formattedSymbol,
    userSettingsSynced,
    buildSavePayload,
    dispatch,
    clearPendingChartPreferencesSave,
  ]);

  // Return the API
  return {
    // Core state
    preferences: chartPreferences,
    setPreference: updateChartPreferences,
    isDirty: chartPreferencesDirty,
    save,
    reset,
    markAsReady,
    
    // Derived global preferences
    axisColorScheme,
    axisColors,
    crosshairSnapMode,
    showCrosshairPriceLabel,
    showAdaptiveGrid,
    showAdminChartControls,
    
    // Derived symbol preferences
    chartPreferencesReadyForSymbol,
    savedChartPreferences,
    showDailyTrendlines,
    showWeeklyTrendlines,
    showHourlyTrendlines,
    
    // Callbacks
    onChartPreferencesChange: handleChartPreferencesChange,
    onToggleWeeklyTrendlines: handleToggleWeeklyTrendlines,
    onToggleDailyTrendlines: handleToggleDailyTrendlines,
    onToggleHourlyTrendlines: handleToggleHourlyTrendlines,
    
    // Reset callback for parent state
    resetPendingTimeframe: onResetPendingTimeframe,
  };
};

export default useChartPreferences;
